import {
  type SxProps,
  Box,
  Stack,
  useTheme,
  DarkTooltip,
} from '@ltvco/refresh-lib/theme';
import {
  CHATAI_LOADING_STATUS,
  ChatAIInput,
  Message,
  useChatForm,
  useFeedback,
} from '@ltvco/refresh-lib/v2';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  TypingIndicator,
  MessageBoxContainer,
  MessageContainerDivider,
  MessagesContainerStack,
  MessagesContentStack,
  MessageContent,
  ChatFeedbackModal,
  GenericReportResponse,
  copyProcessedText,
  CHATAI_GA4_EVENTS,
  LandingSection,
} from '@ltvco/refresh-lib/v2';
import { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { ReportColumn } from './ReportColumn';
import {
  FileCopy,
  FileCopyOutlined,
  ThumbDownOffAlt,
  ThumbUp,
  ThumbUpOffAlt,
  ThumbDown,
} from '@mui/icons-material';
import { theme } from 'theme';

export interface LoadingStatus {
  message: string;
  subtitle: string;
}

export const baseMessageBoxContainerStyles = {
  display: 'block',
  width: '100%',
  maxWidth: '740px',
  backgroundColor: theme.palette.background.paper,
  margin: 0,
  padding: theme.spacing(1, 0),
  overflowWrap: 'break-word',
  boxSizing: 'border-box',
};

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onReportStateChange?: (isOpen: boolean) => void;
  shouldCloseReport?: boolean;
  sx?: SxProps;
  showLanding?: boolean;
  isLogoAnimating?: boolean;
  logoAnimationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  onSendMessage?: (message: string) => void;
  onReportCloseHandled?: () => void;
};

interface MessagePair {
  user: Message;
  assistant: Message | null;
  userIndex: number;
  assistantIndex: number | null;
}

export interface MessageReportData {
  reportData: GenericReportResponse;
  type: string;
  permalink: string;
}

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onReportStateChange,
  shouldCloseReport = false,
  onReportCloseHandled,
  sx,
  showLanding = false,
  isLogoAnimating = false,
  logoAnimationPhase = 'center',
  onSendMessage,
}: MessagesContainerProps) => {
  const theme = useTheme();
  const [openReport, setOpenReport] = useState(false);
  const [messagesContainerWidth, setMessagesContainerWidth] = useState('100%');
  const [permalink, setPermalink] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<
    GenericReportResponse | undefined
  >(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { trackEventGA4, logError } = useContext(AppConfig);
  const { handleSendMessage } = useChatForm(onSendMessage ?? (() => {}));

  // Use the feedback hook
  const {
    openFeedback,
    thumbsUp,
    currentResponseId,
    feedback,
    createFeedback,
    updateFeedback,
    handleOpenFeedback,
    handleCloseFeedback,
  } = useFeedback(messages);
  const { isMobile } = useScreenSize();
  const onReportStateChangeRef = useRef(onReportStateChange);

  // State for smooth transition
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [landingOpacity, setLandingOpacity] = useState(1);
  const [chatOpacity, setChatOpacity] = useState(0);
  const [lastAutoOpenedMessageContent, setLastAutoOpenedMessageContent] =
    useState<string | null>(null);

  const stickyMessageBoxContainerStyles = {
    ...baseMessageBoxContainerStyles,
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflowWrap: 'break-word',
    transition: 'all 0.2s ease-in-out',
  };

  // Update ref when callback changes
  useEffect(() => {
    onReportStateChangeRef.current = onReportStateChange;
  }, [onReportStateChange]);

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const pairRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [tailGap, setTailGap] = useState(0);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);

  const [stickyIndex, setStickyIndex] = useState<number | null>(null);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const visibleAssistantsRef = useRef<Set<number>>(new Set());

  // state for smooth scroll / anti-loop
  const lastMeasuredScrollHeight = useRef<number>(0); // detects real content growth
  const lastAutoScrollTime = useRef<number>(0);
  const prevPairsCountRef = useRef<number>(0); // reliable detection of "new pair"

  // State/refs for smooth scroll
  const smoothScrollRef = useRef<{
    requestAnimationFrameId: number | null;
    target: number | null;
  }>({
    requestAnimationFrameId: null,
    target: null,
  });

  const handleButtonClick = useCallback(
    (message: Message) => (report: MessageReportData) => {
      if (message.reports) {
        setPermalink(report.permalink);
        setReportData(report.reportData);
        setType(report.type);
        setOpenReport(true);
      }
    },
    [],
  );

  // This is used to scroll to the top of the page when a new pair is added
  const smoothScrollTo = useCallback(
    (
      scrollContainer: HTMLElement,
      targetScrollPosition: number,
      animationDuration = 300,
    ) => {
      const scrollAnimationState = smoothScrollRef.current;

      // Cancel any existing animation
      if (scrollAnimationState.requestAnimationFrameId) {
        cancelAnimationFrame(scrollAnimationState.requestAnimationFrameId);
        scrollAnimationState.requestAnimationFrameId = null;
      }

      scrollAnimationState.target = targetScrollPosition;

      const currentScrollPosition = scrollContainer.scrollTop;
      const scrollDistance = targetScrollPosition - currentScrollPosition;
      const animationStartTime = performance.now();

      // Easing function for smooth deceleration
      const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

      const animateScrollStep = (currentTime: number) => {
        // Stop if target has changed
        if (scrollAnimationState.target !== targetScrollPosition) {
          return;
        }

        const animationProgress = Math.min(
          1,
          (currentTime - animationStartTime) / animationDuration,
        );
        const newScrollPosition =
          currentScrollPosition +
          scrollDistance * easeOutCubic(animationProgress);

        scrollContainer.scrollTop = newScrollPosition;

        if (animationProgress < 1) {
          // Continue animation
          scrollAnimationState.requestAnimationFrameId =
            requestAnimationFrame(animateScrollStep);
        } else {
          // Animation complete
          scrollAnimationState.requestAnimationFrameId = null;
        }
      };

      // Start the animation
      scrollAnimationState.requestAnimationFrameId =
        requestAnimationFrame(animateScrollStep);
    },
    [],
  );

  useEffect(() => {
    if (messages.length > 0 && !isMobile) {
      const lastMessage = messages[messages.length - 1];
      // Check if the last message is from assistant and has a report
      if (
        lastMessage.role === 'assistant' &&
        lastMessage.reports?.[0]?.permalink
      ) {
        // Only auto-open if this is a new report (different from the last one we auto-opened)
        if (lastMessage.content !== lastAutoOpenedMessageContent) {
          // Small delay to ensure the message is fully rendered
          const timer = setTimeout(() => {
            handleButtonClick(lastMessage)(lastMessage.reports![0]!);
            setLastAutoOpenedMessageContent(lastMessage.content || null);
          }, 100);

          return () => clearTimeout(timer);
        }
      }
    }
  }, [messages, openReport, lastAutoOpenedMessageContent, handleButtonClick]);

  // Cancel animation if the user interacts
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) {
      return;
    }

    const cancelIfAnimating = () => {
      const smoothScrollState = smoothScrollRef.current;
      if (smoothScrollState.requestAnimationFrameId) {
        cancelAnimationFrame(smoothScrollState.requestAnimationFrameId);
        smoothScrollState.requestAnimationFrameId = null;
        smoothScrollState.target = null;
      }
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleUserScroll = () => {
      // Clear previous timeout to debounce scroll events
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const scrollContainer = scrollRootRef.current;
        if (!scrollContainer) return;

        const { scrollTop } = scrollContainer;
        // Use a smaller threshold for better responsiveness
        const shouldShowSmallFont = scrollTop > 30;

        // Only update state if it actually changed to avoid unnecessary re-renders
        setHasUserScrolled((prev) => {
          if (prev !== shouldShowSmallFont) {
            return shouldShowSmallFont;
          }
          return prev;
        });
      }, 10); // Small debounce delay
    };

    root.addEventListener('wheel', cancelIfAnimating, { passive: true });
    root.addEventListener('touchstart', cancelIfAnimating, { passive: true });
    root.addEventListener('scroll', handleUserScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      root.removeEventListener('wheel', cancelIfAnimating);
      root.removeEventListener('touchstart', cancelIfAnimating);
      root.removeEventListener('scroll', handleUserScroll);
    };
  }, [stickyIndex]); // Add stickyIndex as dependency

  // Pair User and Assistant Messages
  const pairs: MessagePair[] = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role === 'user') {
      const userMsg = messages[i];
      const assistantMsg =
        messages[i + 1]?.role === 'assistant' ? messages[i + 1] : null;
      pairs.push({
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex: assistantMsg ? i + 1 : null,
      });
    }
  }

  //-----Use Callbacks-----

  // Calculate scroll position and determine if user is near bottom
  const calculateScrollPosition = useCallback(
    (scrollContainer: HTMLElement) => {
      const maxScrollTop =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const distanceFromBottom = Math.max(
        0,
        maxScrollTop - scrollContainer.scrollTop,
      );
      const isNearBottom = Math.ceil(distanceFromBottom) <= 20;

      return { maxScrollTop, distanceFromBottom, isNearBottom };
    },
    [],
  );

  // Check if content has grown significantly
  const hasContentGrown = useCallback((scrollContainer: HTMLElement) => {
    const previousHeight = lastMeasuredScrollHeight.current || 0;
    const currentHeight = scrollContainer.scrollHeight;
    const heightGrowth = currentHeight - previousHeight;
    const isSignificantGrowth = heightGrowth > 2;

    lastMeasuredScrollHeight.current = currentHeight;
    return { heightGrowth, isSignificantGrowth };
  }, []);

  // Check if we should ignore auto-scroll (anti-loop protection)
  const shouldIgnoreAutoScroll = useCallback(() => {
    const currentTime = performance.now();
    const timeSinceLastScroll = currentTime - lastAutoScrollTime.current;
    const isTooSoon = timeSinceLastScroll < 48;

    return isTooSoon;
  }, []);

  // Handle auto-scroll based on conditions
  const handleAutoScroll = useCallback(
    (scrollContainer: HTMLElement, hasNewPair: boolean) => {
      const { maxScrollTop, isNearBottom } =
        calculateScrollPosition(scrollContainer);
      const { isSignificantGrowth } = hasContentGrown(scrollContainer);
      const shouldIgnore = shouldIgnoreAutoScroll();

      if (shouldIgnore) {
        return;
      }

      if (hasNewPair) {
        // New pair: smooth animation to bottom
        smoothScrollTo(scrollContainer, maxScrollTop, 300);
        lastAutoScrollTime.current = performance.now();
      } else if (isNearBottom && isSignificantGrowth) {
        // Streaming while near bottom: instant anchor
        scrollContainer.scrollTop = maxScrollTop;
        lastAutoScrollTime.current = performance.now();
      }
    },
    [
      calculateScrollPosition,
      hasContentGrown,
      shouldIgnoreAutoScroll,
      smoothScrollTo,
    ],
  );

  // Calculate the spacer (tailGap) to anchor the last pair to the top
  const adjustScrollGap = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    const lastPair = pairs[pairs.length - 1];

    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;

    const userHeight = userElement?.getBoundingClientRect().height ?? 0;

    let secondHeight = 0;
    if (assistantElement) {
      const assistantHeight = assistantElement.getBoundingClientRect().height;
      const gapHeight = gapRef.current?.getBoundingClientRect().height ?? 0;
      secondHeight = Math.max(0, assistantHeight - gapHeight); // gap is inside → subtract
    } else if (loadingStatus.message !== '') {
      const typingHeight =
        typingRef.current?.getBoundingClientRect().height ?? 0;
      secondHeight = typingHeight;
    }

    const pairHeight = userHeight + secondHeight;
    const availableHeight = Math.max(0, root?.clientHeight ?? 0);
    const gap = Math.max(0, availableHeight - pairHeight);
    setTailGap(gap);
  }, [pairs.length, loadingStatus.message]);

  // Sticky header
  useEffect(() => {
    // Clear the previous set of visible assistants
    visibleAssistantsRef.current.clear();

    // Create intersection observer to detect visible messages
    const intersectionObserver = new IntersectionObserver(
      (intersectionEntries) => {
        intersectionEntries.forEach((entry) => {
          const currentAssistantIndex = Number(
            entry.target.getAttribute('data-index'),
          );
          if (Number.isFinite(currentAssistantIndex)) {
            if (entry.isIntersecting) {
              visibleAssistantsRef.current.add(currentAssistantIndex);
            } else {
              visibleAssistantsRef.current.delete(currentAssistantIndex);
            }
          }
        });

        // Calculate the sticky index based on the visible assistants
        if (visibleAssistantsRef.current.size > 0) {
          const visibleUserMessageIndexes = Array.from(
            visibleAssistantsRef.current,
          )
            .map((visibleAssistantIndex) => {
              const messagePair = pairs.find(
                (pair) => pair.assistantIndex === visibleAssistantIndex,
              );
              return messagePair ? messagePair.userIndex : null;
            })
            .filter((userIndex): userIndex is number => userIndex !== null);

          if (visibleUserMessageIndexes.length > 0) {
            const minimumUserIndex = Math.min(...visibleUserMessageIndexes);
            setStickyIndex(minimumUserIndex);
          } else {
            setStickyIndex(null);
          }
        } else {
          setStickyIndex(null);
        }
      },
      { root: scrollRootRef.current || null, threshold: 0.1 },
    );

    // Observe each assistant message to detect visibility
    pairs.forEach((messagePair) => {
      if (messagePair.assistantIndex !== null) {
        const assistantMessageElement =
          messageRefs.current[messagePair.assistantIndex];
        if (assistantMessageElement) {
          assistantMessageElement.setAttribute(
            'data-index',
            messagePair.assistantIndex.toString(),
          );
          intersectionObserver.observe(assistantMessageElement);
        }
      }
    });

    // Clean up the observer when unmounting
    return () => intersectionObserver.disconnect();
  }, [messages]);

  // ResizeObserver to keep layout consistent
  // This is used to keep the layout consistent when the window is resized
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    // Detect new pair by counting
    const gotNewPair = pairs.length > prevPairsCountRef.current;
    prevPairsCountRef.current = pairs.length;

    // Last pair elements already mounted
    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;
    const typingElement =
      lastPair.assistantIndex === null ? typingRef.current : null;

    if (resizeObsRef.current) {
      resizeObsRef.current.disconnect();
    }
    const resizeObserver = new ResizeObserver(() => {
      // Let browser apply layout changes first
      requestAnimationFrame(() => {
        adjustScrollGap();

        // Now handle scroll after layout is stable
        requestAnimationFrame(() => {
          handleAutoScroll(root, gotNewPair);
        });
      });
    });

    resizeObserver.observe(root);
    if (userElement) {
      resizeObserver.observe(userElement);
    }
    if (assistantElement) {
      resizeObserver.observe(assistantElement);
    }
    if (!assistantElement && typingElement) {
      resizeObserver.observe(typingElement);
    }

    resizeObsRef.current = resizeObserver;

    // Initial calculation and anchor
    requestAnimationFrame(() => {
      adjustScrollGap();
      requestAnimationFrame(() => {
        lastMeasuredScrollHeight.current = root.scrollHeight;
        handleAutoScroll(root, gotNewPair);
      });
    });

    return () => resizeObserver.disconnect();
  }, [
    pairs.length,
    adjustScrollGap,
    openReport,
    messagesContainerWidth,
    smoothScrollTo,
  ]);

  const handleCloseReport = () => {
    trackEventGA4({
      event_name: CHATAI_GA4_EVENTS.name,
      type: CHATAI_GA4_EVENTS.report.type,
      sub_type: CHATAI_GA4_EVENTS.report.subTypes.close_report,
    });
    setOpenReport(false);
    setPermalink(undefined);
    setReportData(undefined);
    setType(undefined);
  };

  const handleCopyClick = async (content: string, messageIndex: number) => {
    try {
      const success = await copyProcessedText(content);
      if (success) {
        trackEventGA4({
          event_name: CHATAI_GA4_EVENTS.name,
          type: CHATAI_GA4_EVENTS.message.type,
          sub_type: CHATAI_GA4_EVENTS.message.subTypes.copy_response,
        });
        // Clear any existing timeout to prevent multiple timeouts running simultaneously
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setCopiedIndex(messageIndex);
        timeoutRef.current = setTimeout(() => {
          setCopiedIndex(null);
        }, 1000);
      }
    } catch (error) {
      logError('Error copying text', error as Error);
    }
  };

  // Handle smooth transition between landing and chat
  useEffect(() => {
    if (showLanding) {
      // When showing landing, fade in landing and fade out chat
      setLandingOpacity(1);
      setChatOpacity(0);
    } else {
      // When hiding landing, show chat immediately
      setLandingOpacity(0);
      setChatOpacity(1);
    }
    setIsTransitioning(false);
  }, [showLanding]);

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (openReport) {
      setMessagesContainerWidth('60%');
    } else {
      setMessagesContainerWidth('100%');
    }
  }, [openReport]);

  useEffect(() => {
    onReportStateChangeRef.current?.(openReport);
  }, [openReport]);

  // Handle external report close request
  useEffect(() => {
    if (!shouldCloseReport) return;
    if (openReport) {
      handleCloseReport();
    }
    onReportCloseHandled?.();
  }, [shouldCloseReport, openReport, handleCloseReport, onReportCloseHandled]);

  return (
    <Stack direction={openReport ? 'row' : 'column'} width="100%" height="100%">
      {!isMobile || !openReport ? (
        <MessagesContainerStack
          spacing={2}
          sx={{
            width: messagesContainerWidth,
            height: openReport ? '98%' : '100%',
            position: 'relative',
            pb: { xs: 3, md: 0 },
            ...sx,
          }}
        >
          <LandingSection
            showLanding={showLanding}
            landingOpacity={landingOpacity}
            isTransitioning={isTransitioning}
            isLogoAnimating={isLogoAnimating}
            logoAnimationPhase={logoAnimationPhase}
            onSendMessage={handleSendMessage}
          />
          {!showLanding && (
            <MessagesContentStack
              ref={scrollRootRef}
              sx={{
                opacity: chatOpacity,
                pointerEvents: chatOpacity === 0 ? 'none' : 'auto',
              }}
            >
              {pairs.map((pair, index) => (
                <Stack
                  key={index}
                  ref={(el) => {
                    pairRefs.current[index] = el;
                  }}
                  width="100%"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MessageBoxContainer
                    ref={(el: HTMLDivElement | null) => {
                      messageRefs.current[pair.userIndex] = el;
                    }}
                    sx={
                      stickyIndex === pair.userIndex
                        ? stickyMessageBoxContainerStyles
                        : baseMessageBoxContainerStyles
                    }
                  >
                    <MessageContent
                      content={pair.user.content ?? ''}
                      isHeader={true}
                      isSticky={stickyIndex === pair.userIndex}
                      hasUserScrolled={hasUserScrolled}
                      handleButtonClick={handleButtonClick(pair.user)}
                      reports={pair.user.reports}
                      currentPermalink={permalink}
                    />
                  </MessageBoxContainer>
                  {pair.assistant ? (
                    <MessageBoxContainer
                      ref={(el: HTMLDivElement | null) => {
                        messageRefs.current[pair.assistantIndex!] = el;
                      }}
                      sx={{
                        width: '100%',
                        maxWidth: '740px',
                      }}
                    >
                      <MessageContent
                        content={pair.assistant.content ?? ''}
                        isHeader={false}
                        isSticky={false}
                        hasUserScrolled={hasUserScrolled}
                        handleButtonClick={handleButtonClick(pair.assistant!)}
                        reports={pair.assistant.reports}
                        currentPermalink={permalink}
                      />
                      <Box>
                        <Stack
                          direction={'row'}
                          spacing={2}
                          justifyContent={'end'}
                        >
                          {feedback[pair.assistant.response_id || '']
                            ?.thumbs_up ? (
                            <ThumbUp
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                true,
                                pair.assistant.response_id || '',
                              )}
                            />
                          ) : (
                            <ThumbUpOffAlt
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                true,
                                pair.assistant.response_id || '',
                              )}
                            />
                          )}
                          {feedback[pair.assistant.response_id || '']
                            ?.thumbs_up === false ? (
                            <ThumbDown
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                false,
                                pair.assistant.response_id || '',
                              )}
                            />
                          ) : (
                            <ThumbDownOffAlt
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                false,
                                pair.assistant.response_id || '',
                              )}
                            />
                          )}
                          <DarkTooltip
                            title="Copied!"
                            disableHoverListener
                            disableFocusListener
                            open={copiedIndex === pair.assistantIndex}
                          >
                            {copiedIndex === pair.assistantIndex ? (
                              <FileCopy
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: '16px',
                                }}
                              />
                            ) : (
                              <FileCopyOutlined
                                onClick={() =>
                                  handleCopyClick(
                                    pair.assistant!.content || '',
                                    pair.assistantIndex!,
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCopyClick(
                                      pair.assistant!.content || '',
                                      pair.assistantIndex!,
                                    );
                                  }
                                }}
                                aria-label="Copy message"
                                tabIndex={3}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </DarkTooltip>
                        </Stack>
                        <MessageContainerDivider />
                      </Box>
                      {/* Tail Gap inside the last block */}
                      {index === pairs.length - 1 && (
                        <Box
                          ref={(el: HTMLDivElement | null) => {
                            gapRef.current = el;
                          }}
                          sx={{ height: `${tailGap}px`, width: '100%' }}
                        />
                      )}
                    </MessageBoxContainer>
                  ) : (
                    <>
                      <Box
                        ref={typingRef}
                        sx={{ width: '100%', maxWidth: '740px' }}
                      >
                        <TypingIndicator loadingStatus={loadingStatus} />
                      </Box>
                      {index === pairs.length - 1 && (
                        <Box
                          ref={(el: HTMLDivElement | null) => {
                            gapRef.current = el;
                          }}
                          sx={{ height: `${tailGap}px`, width: '100%' }}
                        />
                      )}
                    </>
                  )}
                </Stack>
              ))}
            </MessagesContentStack>
          )}
          {!isMobile && !showLanding && (
            <ChatAIInput
              isLoading={
                loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message
              }
            />
          )}
        </MessagesContainerStack>
      ) : null}
      {openReport && (
        <ReportColumn
          reportData={reportData}
          onClose={handleCloseReport}
          permalink={permalink}
          type={type}
        />
      )}
      <ChatFeedbackModal
        open={openFeedback}
        onClose={handleCloseFeedback}
        thumbsUp={thumbsUp}
        responseItemId={currentResponseId}
        createFeedback={createFeedback}
        updateFeedback={updateFeedback}
        hasRecord={typeof feedback[currentResponseId]?.thumbs_up === 'boolean'}
        feedbackText={
          feedback[currentResponseId]?.feedback?.feedback_text || ''
        }
      />
    </Stack>
  );
};
